import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useFileDownload,
  useLegalClientSaveSigningContract,
} from 'api/requests';
import { FileUpload, useFilesUpload, useServerErrorNotify } from 'hooks';

import { SigningContractFormType } from '../forms/signingContractForm';

import { useOnboardingContext } from './useOnboardingContext';

export const useSigningContract = () => {
  const { data, goToBack, goToNext } = useOnboardingContext();
  const { getFileLink } = useFileDownload();

  const {
    signingContract,
    loading: submitLoading,
    error: submitError,
  } = useLegalClientSaveSigningContract();

  const contractFile = useMemo(
    () => data.legalClient?.clientServiceAgreementFiles_Global?.companySigned,
    [data.legalClient?.clientServiceAgreementFiles_Global?.companySigned]
  );

  const [contractFileHref, setContractFileHref] = useState<string | null>(null);

  const initialFiles = useRef(
    data.legalClient?.clientServiceAgreementFiles_Global?.fullySigned
      ? [
          new FileUpload(
            {
              name: data.legalClient?.clientServiceAgreementFiles_Global
                ?.fullySigned.name,
              size: data.legalClient?.clientServiceAgreementFiles_Global
                ?.fullySigned.size,
            },
            undefined,
            false,
            data.legalClient?.clientServiceAgreementFiles_Global?.fullySigned.id
          ),
        ]
      : undefined
  ).current;

  const initialValues: SigningContractFormType = useRef({
    fileIds: data.legalClient?.signedQuestionnaireFiles?.map((v) => v.id) ?? [],
  }).current;

  const {
    files,
    isLoadingFile: submitDisabled,
    onRemoveFile,
    onAddFiles,
  } = useFilesUpload(initialFiles);

  const onSubmit = async (values: SigningContractFormType) => {
    await signingContract(data.legalClient?.id!, values.fileIds[0]);
    goToNext();
  };

  useEffect(() => {
    if (contractFile?.id) {
      getFileLink(contractFile.id).then((link) => {
        if (link) {
          setContractFileHref(link);
        }
      });
    }
  }, [getFileLink, contractFile?.id]);

  useServerErrorNotify(submitError);

  return {
    initialValues,
    files,
    submitLoading,
    submitDisabled,
    contractFileHref,
    goToBack,
    onSubmit,
    onAddFiles,
    onRemoveFile,
  };
};
